import { useTable } from "@/components/shared/data-table/useTable";
import {
  type RawFilters,
  filterItemWithRawFilters,
  useServerSideQuery,
} from "@/hooks/useQueryFilters";
import type { GetAllInvoiceInvoice } from "@/queries/invoices/get-all";
import { useGetAllInvoices } from "@/queries/invoices/get-all";
import type { ColumnDef } from "@tanstack/react-table";
import { useState } from "react";

type Props = {
  storageKey: string;
  defaultFilters: RawFilters<keyof GetAllInvoiceInvoice>;
  columns: ColumnDef<GetAllInvoiceInvoice>[];
};

export function useInvoicesForTable({
  storageKey,
  defaultFilters,
  columns,
}: Props) {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);

  const {
    globalSearch,
    setGlobalSearch,
    serverSideFilters,
    setFilters,
    filters,
    sorting,
    onSortingChange,
    sort,
    order,
    resetFilters,
    initialServerSideFilters,
  } = useServerSideQuery<any>({
    storageKey,
    defaultFilters,
    defaultSorting: {
      sort: "createdAt",
      order: "desc",
    },
    queryKeyPrefix: storageKey,
  });

  const {
    data: invoicesData,
    loading,
    pagination,
  } = useGetAllInvoices({
    query: serverSideFilters,
    global: globalSearch,
    page,
    limit,
    sort,
    order,
  });

  const invoices = invoicesData?.map((invoice) => invoice.data) || [];

  const filteredInvoices = invoices.filter((invoice) =>
    filterItemWithRawFilters(invoice.invoice, defaultFilters),
  );

  const {
    table,
    clickableColumns,
    columnVisibility,
    data,
    stickyColumns,
    footer,
  } = useTable({
    data: filteredInvoices,
    columns,
    tableName: storageKey,
    getRowId: (row) => row.invoiceDetailsId,
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    sorting,
    onSortingChange,
    initialServerSideFilters,
    onServerSideFiltersClear: resetFilters,
    serverSideFilters,
    globalServerSideSearch: globalSearch,
    stickyColumns: {
      left: ["select", "supplierName"],
      right: ["actions"],
    },
  });

  return {
    table,
    loading,
    pagination,
    filters,
    setFilters,
    globalSearch,
    setGlobalSearch,
    clickableColumns,
    columnVisibility,
    data,
    stickyColumns,
    footer,
    setPage,
    setLimit,
  };
}
